
import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatDateRangePicker} from '@angular/material/datepicker';
import {MatSelectChange} from '@angular/material/select';
import {FilterOptions, MenuFilterRequest} from '../../../interface/menuFilterRequest';

export interface MenuFilterDialogData {
  activeTab: string;
  currentFilters: MenuFilterRequest;
  filterOptions: FilterOptions;
}

export interface MenuFilterDialogResult {
  action: 'filter' | 'reset';
  filters: MenuFilterRequest;
}

@Component({
  selector: 'app-menu-filter-dialog',
  templateUrl: './menu-filter-dialog.component.html',
  styleUrl: './menu-filter-dialog.component.scss'
})
export class MenuFilterDialogComponent implements OnInit {
  selectedBusinessName: string = '';
  selectedMenuName: string = '';
  selectedAddress: string = '';
  selectedCardUsernames: string[] = [];
  selectedType: string = '';
  selectedDateRange: string = '';
  customStartDate: Date | null = null;
  customEndDate: Date | null = null;

  filteredBusinessNames: string[] = [];
  filteredMenuNames: string[] = [];
  filteredAddresses: string[] = [];
  @ViewChild('customRangePicker') customRangePicker?: MatDateRangePicker<Date>;

  constructor(
      public dialogRef: MatDialogRef<MenuFilterDialogComponent>,
      @Inject(MAT_DIALOG_DATA) public data: MenuFilterDialogData
  ) {
  }

  ngOnInit() {
    if (this.data.currentFilters) {
      this.selectedBusinessName = this.data.currentFilters.businessName || '';
      this.selectedMenuName = this.data.currentFilters.menuName || '';
      this.selectedAddress = this.data.currentFilters.address || '';
      this.selectedCardUsernames = this.data.currentFilters.cardUsernames || [];
      this.selectedType = this.data.currentFilters.type || '';
      this.customStartDate = this.parseDateValue(this.data.currentFilters.dateFrom);
      this.customEndDate = this.parseDateValue(this.data.currentFilters.dateTo);
      if (this.customStartDate || this.customEndDate) {
        this.selectedDateRange = 'custom';
      }
    }
    this.filteredBusinessNames = this.data.filterOptions.businessNames || [];
    this.filteredMenuNames = this.data.filterOptions.menuNames || [];
    this.filteredAddresses = this.data.filterOptions.addresses || [];
  }

  applyFilter() {
    const filters: MenuFilterRequest = {};
    if (this.selectedBusinessName) {
      filters.businessName = this.selectedBusinessName;
    }
    if (this.selectedMenuName) {
      filters.menuName = this.selectedMenuName;
    }
    if (this.selectedAddress) {
      filters.address = this.selectedAddress;
    }
    if (this.selectedCardUsernames.length > 0) {
      filters.cardUsernames = this.selectedCardUsernames;
    }
    if (this.selectedType) {
      filters.type = this.selectedType;
    }

    this.resolveDateRange(filters);

    const result: MenuFilterDialogResult = {
      action: 'filter',
      filters: filters
    };
    this.dialogRef.close(result);
  }

  private resolveDateRange(filters: MenuFilterRequest) {
    const now = new Date();
    if (this.selectedDateRange === 'today') {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      filters.dateFrom = this.formatDateOnly(start);
      filters.dateTo = this.formatDateOnly(now);
    } else if (this.selectedDateRange === 'week') {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      start.setHours(0, 0, 0, 0);
      filters.dateFrom = this.formatDateOnly(start);
      filters.dateTo = this.formatDateOnly(now);
    } else if (this.selectedDateRange === 'month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      filters.dateFrom = this.formatDateOnly(start);
      filters.dateTo = this.formatDateOnly(now);
    } else if (this.selectedDateRange === 'custom') {
      if (this.customStartDate) {
        filters.dateFrom = this.formatDateOnly(this.customStartDate);
      }
      if (this.customEndDate) {
        filters.dateTo = this.formatDateOnly(this.customEndDate);
      }
    }
  }

  onDateRangeTypeChange(event: MatSelectChange): void {
    if (event.value === 'custom') {
      this.openCustomRangePicker();
    } else {
      this.customStartDate = null;
      this.customEndDate = null;
    }
  }

  openCustomRangePicker(): void {
    setTimeout(() => this.customRangePicker?.open());
  }

  hasCustomRangeValue(): boolean {
    return this.selectedDateRange === 'custom' && !!(this.customStartDate || this.customEndDate);
  }

  getCustomRangeDisplayValue(): string {
    const start = this.customStartDate ? this.formatDisplayDate(this.customStartDate) : '';
    const end = this.customEndDate ? this.formatDisplayDate(this.customEndDate) : '';
    return start && end ? `${start} - ${end}` : (start || end);
  }

  resetDateRange(picker: MatDateRangePicker<Date>): void {
    this.customStartDate = null;
    this.customEndDate = null;
    picker.close();
  }

  resetFilters() {
    const result: MenuFilterDialogResult = {
      action: 'reset',
      filters: {}
    };
    this.dialogRef.close(result);
  }

  cancel() {
    this.dialogRef.close(null);
  }

  private parseDateValue(value?: string): Date | null {
    if (!value) {
      return null;
    }
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  private formatDateOnly(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatDisplayDate(date: Date): string {
    return date.toLocaleDateString('en-US');
  }

}
